import { domainId as toDomainId } from '@thom/folio-domain/core/domain/domain'
import type { Project } from '@thom/folio-domain/core/domain/project'
import { projectId as toProjectId } from '@thom/folio-domain/core/domain/project'
import type { ProjectFilter, ProjectsPort } from '@thom/folio-domain/core/ports/projects'
import { err, ok, type Result } from '@thom/folio-domain/lib/result'
import { tryCatch } from '@thom/folio-domain/lib/try-catch'
import { Collections, type JournProjectsResponse } from '@thom/folio-ui/pocketbase-types'
import { getPocketBaseClient } from './client'
import { keyed } from './request-key'
import { filterFor } from '@thom/pb/filter-builder'
import { paginate } from './paginate'

type ProjectRecord = JournProjectsResponse

type ProjectColumns = {
	domain: string
	slug: string
	name: string
	descr: string
	archived: boolean
	created: Date
	updated: Date
}

const columns = (filter: ProjectFilter) =>
	filterFor<ProjectColumns>()([
		{ field: 'domain', comparator: 'eq', value: filter.domainId },
		{ field: 'archived', comparator: 'eq', value: filter.includeArchived ? undefined : false },
		{ field: 'name', comparator: 'contains', value: filter.search },
	])

const toProject = (record: ProjectRecord): Project => ({
	id: toProjectId(record.id),
	domainId: toDomainId(record.domain ?? ''),
	slug: record.slug,
	name: record.name,
	descr: record.descr ?? '',
	archived: record.archived ?? false,
	createdAt: new Date(record.created),
	updatedAt: new Date(record.updated),
})

const createProjectsAdapter = (): ProjectsPort => {
	const client = getPocketBaseClient()
	const projects = () => client.collection(Collections.JournProjects)

	return {
		list: async (filter: ProjectFilter = {}): Promise<Result<readonly Project[]>> => {
			const { expr, params } = columns(filter)

			const { data, error } = await tryCatch(
				paginate<ProjectRecord>(
					projects(),
					filter,
					keyed('projects.list', {
						filter: client.filter(expr, params),
						sort: 'name',
					}),
				),
			)

			return error
				? err(new Error(`Failed to list projects: ${error.message}`, { cause: error }))
				: ok(data.map(toProject))
		},

		get: async (ref: string): Promise<Result<Project>> => {
			const { expr, params } = filterFor<ProjectColumns>()([{ field: 'slug', comparator: 'eq', value: ref }])

			const { data, error } = await tryCatch(
				projects().getFirstListItem<ProjectRecord>(client.filter(expr, params), keyed('projects.get', {})),
			)

			return error
				? err(new Error(`Failed to load project ${ref}: ${error.message}`, { cause: error }))
				: ok(toProject(data))
		},
	}
}

export { columns, createProjectsAdapter, toProject }
